import { Plus, Trash2 } from 'lucide-react'
import type { Tier } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import { Input } from '@/components/ui/Field'
import TierBadge from '@/components/ui/TierBadge'
import Toggle from '@/components/ui/Toggle'
import { cn } from '@/lib/cn'
import { nextGroupKey, sizeGroupsProblem, sizeList, type Draft, type PatchDraft, type SizeGroupDraft } from './draft'

/** Tier prices, sizes priced apart (Kids) and the name & number charge. */
export default function PricingSection({ draft, patch, tiers }: { draft: Draft; patch: PatchDraft; tiers: Tier[] }) {
  const patchGroup = (index: number, change: Partial<SizeGroupDraft>) =>
    patch({ size_groups: draft.size_groups.map((group, i) => (i === index ? { ...group, ...change } : group)) })

  // Renaming a group carries each special customer's figure for it along,
  // since those are keyed by the group's name.
  const renameGroup = (index: number, name: string) => {
    const old = draft.size_groups[index].name
    patch({
      size_groups: draft.size_groups.map((group, i) => (i === index ? { ...group, name } : group)),
      customer_prices: draft.customer_prices.map((row) => {
        if (!(old in row.groupPrices)) return row
        const { [old]: amount, ...rest } = row.groupPrices
        return { ...row, groupPrices: { ...rest, [name]: amount } }
      }),
    })
  }

  const groupProblem = sizeGroupsProblem(draft.size_groups, sizeList(draft.sizes), tiers.map((tier) => tier.id))

  return (
    <div className="space-y-5">
      <Card>
        <SectionLabel>Tier prices (RM per piece)</SectionLabel>
        <div className="grid gap-2 sm:grid-cols-3">
          {tiers.map((tier) => (
            <div key={tier.id} className="rounded-lg border border-line p-2.5">
              <TierBadge name={tier.name} color={tier.color} icon={tier.icon} />
              <Input
                type="number"
                min={0}
                step="0.5"
                className="mt-2"
                placeholder="RM"
                value={draft.tier_prices[tier.id] ?? ''}
                onChange={(e) => patch({ tier_prices: { ...draft.tier_prices, [tier.id]: e.target.value } })}
              />
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-2xs text-muted">Flat price the customer pays per piece at each tier.</p>
      </Card>

      <Card>
        <SectionLabel>Size price groups</SectionLabel>
        {draft.size_groups.length > 0 && (
          <div className="space-y-2">
            {draft.size_groups.map((group, index) => (
              <div key={group.key} className="rounded-lg border border-line p-2.5">
                <div className="flex items-center gap-2">
                  <Input
                    className="flex-1"
                    placeholder="Group name, e.g. Kids"
                    aria-label="Size group name"
                    maxLength={40}
                    value={group.name}
                    onChange={(e) => renameGroup(index, e.target.value)}
                  />
                  <button
                    type="button"
                    title={`Remove ${group.name || 'this group'}`}
                    onClick={() => patch({ size_groups: draft.size_groups.filter((_, i) => i !== index) })}
                    className="shrink-0 rounded-lg border border-line p-2 text-muted transition hover:text-danger"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {sizeList(draft.sizes).map((size) => {
                    const on = group.sizes.includes(size)
                    const elsewhere = draft.size_groups.find((other, i) => i !== index && other.sizes.includes(size))
                    return (
                      <button
                        key={size}
                        type="button"
                        aria-pressed={on}
                        disabled={!on && Boolean(elsewhere)}
                        title={!on && elsewhere ? `In ${elsewhere.name || 'another group'}` : undefined}
                        onClick={() => patchGroup(index, { sizes: on ? group.sizes.filter((s) => s !== size) : [...group.sizes, size] })}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-xs font-semibold transition disabled:opacity-40',
                          on ? 'border-primary bg-primary text-white' : 'border-line text-foreground/70 hover:border-primary',
                        )}
                      >
                        {size}
                      </button>
                    )
                  })}
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {tiers.map((tier) => (
                    <div key={tier.id}>
                      <TierBadge name={tier.name} color={tier.color} icon={tier.icon} />
                      <Input
                        type="number"
                        min={0.01}
                        step="0.5"
                        className="mt-1"
                        placeholder="RM"
                        aria-label={`${group.name || 'Group'} price for ${tier.name}`}
                        value={group.prices[tier.id] ?? ''}
                        onChange={(e) => patchGroup(index, { prices: { ...group.prices, [tier.id]: e.target.value } })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() =>
            patch({
              size_groups: [
                ...draft.size_groups,
                { key: nextGroupKey(), name: draft.size_groups.length === 0 ? 'Kids' : '', sizes: [], prices: {} },
              ],
            })
          }
          className={cn('inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline', draft.size_groups.length > 0 && 'mt-2')}
        >
          <Plus className="size-3.5" /> Add size group
        </button>
        {groupProblem && <p className="mt-1.5 text-2xs text-danger">{groupProblem}</p>}
        <p className="mt-1.5 text-2xs text-muted">
          Sizes that cost differently, e.g. Kids 22-32, with their own price per tier. Sizes in no group pay the tier
          price above. Option add-ons, name and number, and urgent charges still apply on top.
        </p>
      </Card>

      <Card>
        <Toggle
          switchRight
          checked={draft.allow_personalization}
          onChange={(allow_personalization) => patch({ allow_personalization })}
          label="Name & number"
          hint="Customers can print a name and number on each piece."
        />
        {/* Laid out like the toggle row above: words left, the box at the right edge. */}
        {draft.allow_personalization && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-line pt-4">
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">Charge per piece</span>
              <span className="block text-xs text-muted">Added when the customer chooses it. Leave blank for free.</span>
            </span>
            {/* Width on a wrapper: inputClass carries w-full, which cn() cannot override. */}
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="text-xs text-muted">RM</span>
              <div className="w-24">
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  placeholder="0.00"
                  aria-label="Name & number charge per piece"
                  value={draft.personalization_price}
                  onChange={(e) => patch({ personalization_price: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
